import React, { useState, useEffect } from 'react';
import api from '../api';
import { FaSolarPanel, FaHome, FaBolt, FaRupeeSign, FaCheckCircle } from 'react-icons/fa';

const NetMetering = () => {
  const [data, setData] = useState(null);

  // --- ICON MAP ---
  const iconMap = {
    "FaSolarPanel": <FaSolarPanel />,
    "FaHome": <FaHome />,
    "FaBolt": <FaBolt />,
    "FaRupeeSign": <FaRupeeSign />
  };



  useEffect(() => {
api.get('/NetMetering').then(res => setData(res.data)).catch(console.error);
  }, []);

  if (!data || !data.settings) return <div className="text-center py-5">Loading Net Metering Data...</div>;

  const { steps, benefits, settings } = data;

  return (
    <section className="net-metering py-5 bg-white" id="net-metering">
      <div className="container">
        {/* Section Header */}
        <div className="text-center mb-5">
          <span className="badge bg-primary mb-3 px-3 py-2">
            <i className="bi bi-arrow-left-right me-2"></i>
            Net Metering Explained
          </span>
          <h2 className="display-5 fw-bold text-dark mb-3">
            How Net Metering Works
          </h2>
          <p className="lead text-muted">
            Understanding bidirectional energy flow and billing
          </p>
        </div>

        <div className="row g-4 mb-5">
          {/* Process Flow */}
          <div className="col-lg-7">
            <div className="net-metering-diagram">
              <h4 className="fw-bold mb-4" style={{ color: '#1B3F6E' }}>
                The Net Metering Process 
              </h4>

              {/* Dynamic Steps */}
              {steps.map((step, index) => (
                <div className="d-flex mb-4" key={index}>
                  <div 
                    className="step-icon-wrapper flex-shrink-0 me-3"
                    style={{
                      width: '60px', height: '60px',
                      background: step.colorHex,
                      borderRadius: '12px',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: '1.8rem', color: '#ffffff',
                      boxShadow: `0 4px 15px ${step.colorHex}40`,
                      position: 'relative'
                    }}
                  >
                    {iconMap[step.iconName] || <FaBolt />}
                    <div className="step-number" style={{ position: 'absolute', top: '-10px', right: '-10px', width: '30px', height: '30px', background: '#1B3F6E', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.9rem', fontWeight: 'bold', color: '#ffffff' }}>
                      {index + 1}
                    </div>
                  </div>
                  <div className="flex-grow-1">
                    <h5 className="fw-bold mb-2" style={{ color: '#1B3F6E' }}>{step.title}</h5>
                    <p className="text-muted mb-0">{step.description}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Visual Flow Chart (Static Icons, visual logic remains same) */}
            <div className="mt-4 p-4 rounded" style={{ background: '#F8F9FA' }}>
              <div className="text-center">
                <div className="d-flex justify-content-around align-items-center flex-wrap">
                  <div className="text-center mb-3 mb-md-0">
                    <div className="mx-auto mb-2" style={{ width: '80px', height: '80px', background: '#FF9800', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '2rem', color: '#ffffff' }}>☀️</div>
                    <small className="fw-bold">Solar Panels</small>
                  </div>
                  <div className="d-none d-md-block"><i className="bi bi-arrow-right" style={{ fontSize: '2rem', color: '#00D084' }}></i></div>
                  <div className="text-center mb-3 mb-md-0">
                    <div className="mx-auto mb-2" style={{ width: '80px', height: '80px', background: '#2196F3', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '2rem', color: '#ffffff' }}>⚡</div>
                    <small className="fw-bold">Net Meter</small>
                  </div>
                  <div className="d-none d-md-block"><i className="bi bi-arrow-left-right" style={{ fontSize: '2rem', color: '#00D084' }}></i></div>
                  <div className="text-center">
                    <div className="mx-auto mb-2" style={{ width: '80px', height: '80px', background: '#4CAF50', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '2rem', color: '#ffffff' }}>🏠</div>
                    <small className="fw-bold">Your Home/Grid</small>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Benefits & Info */}
          <div className="col-lg-5">
            {/* Benefits Card */}
            <div className="card border-0 shadow-lg mb-4" style={{ background: 'linear-gradient(135deg, #E8F8F3 0%, #E3F2FD 100%)' }}>
              <div className="card-body p-4">
                <h5 className="fw-bold mb-3" style={{ color: '#1B3F6E' }}>
                  <i className="bi bi-check-circle-fill text-success me-2"></i> Net Metering Benefits
                </h5>
                <ul className="list-unstyled mb-0">
                  {benefits.map((benefit, index) => (
                    <li className="mb-3 d-flex align-items-start" key={index}>
                      <i className="bi bi-check-lg text-success me-2 mt-1"></i>
                      <span className="text-muted">{benefit.benefitText}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Example Calculation (Dynamic from Settings) */}
            <div className="card border-0 shadow-sm">
              <div className="card-body p-4">
                <h5 className="fw-bold mb-3" style={{ color: '#1B3F6E' }}>
                  <i className="bi bi-calculator text-primary me-2"></i> Example: How It Works
                </h5>
                <div className="mb-3">
                  <div className="d-flex justify-content-between mb-2">
                    <span className="text-muted">Day: Solar Generation</span>
                    <span className="fw-bold text-success">{settings.ex_Gen}</span>
                  </div>
                  <div className="d-flex justify-content-between mb-2">
                    <span className="text-muted">Day: Your Consumption</span>
                    <span className="fw-bold text-danger">{settings.ex_Cons}</span>
                  </div>
                  <div className="d-flex justify-content-between mb-2">
                    <span className="text-muted">Export to Grid</span>
                    <span className="fw-bold text-primary">{settings.ex_Export}</span>
                  </div>
                  <hr />
                  <div className="d-flex justify-content-between mb-2">
                    <span className="text-muted">Night: Import from Grid</span>
                    <span className="fw-bold text-danger">{settings.ex_Import}</span>
                  </div>
                  <hr className="border-2" />
                  <div className="d-flex justify-content-between">
                    <span className="fw-bold">Net Bill for Month</span>
                    <span className="fw-bold text-success">{settings.ex_Net}</span>
                  </div>
                </div>
                <div className="alert alert-success mb-0" role="alert">
                  <small>
                    <i className="bi bi-info-circle-fill me-2"></i>
                    {settings.ex_Note}
                  </small>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* RENGIE Support CTA (Dynamic from Settings) */}
        <div className="row">
          <div className="col-lg-8 mx-auto">
            <div className="card border-0 shadow-lg" style={{ background: 'linear-gradient(135deg, #1B3F6E 0%, #2E5C8A 100%)' }}>
              <div className="card-body p-4 text-center text-white">
                <h5 className="fw-bold mb-3">{settings.ctA_Title}</h5>
                <p className="mb-4 opacity-90">{settings.ctA_Desc}</p>
                <a href="#contact" className="btn btn-light btn-lg rounded-pill px-5" style={{ color: '#1B3F6E' }}>
                  <i className="bi bi-telephone-fill me-2"></i> Talk to Our Expert
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default NetMetering;